import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CVRParticipant, GraphEdge, GraphNode, OwnershipGraphData, PersonDetails } from '../../types'
import {
  getCompanyDetails,
  getOwnershipGraphDownstream,
  getOwnershipGraphUpstream,
  getPersonDetails,
} from '../../services/apiClient'
import { formatPercentRange } from '../../utils/format'
import { layoutTree, type PositionedNode } from './ownership/layoutTree'
import {
  canExpandDown,
  canExpandDownRoot,
  canExpandUp,
} from './ownership/graphExpandEligibility'
import {
  buildOwnershipGraphSvg,
  downloadOwnershipGraphSvg,
  ownershipGraphSvgFilename,
} from './ownership/graphSvgExport'
import { mergeGraphData } from './ownership/mergeGraphData'
import GraphViewport from './ownership/GraphViewport'
import GraphNodePanel from './ownership/GraphNodePanel'
import {
  buildCurvedOwnerEdge,
  computeLaneOffsetXPerEdge,
  labelBoxForText,
  buildHighlightLineThrough,
  deconflictLabelsOnCurves,
  intermediateNodesSkippedByEdge,
  type CurvedEdgeGeom,
} from './ownership/edgeGeometry'

// ---------------------------------------------------------------------------
// Colours pulled from the editorial palette (matches tailwind.config.js)
// ---------------------------------------------------------------------------
const C = {
  ink:          '#1A1A1A',
  ink500:       '#666664',
  ink400:       '#8A8A86',
  rule:         '#E5E2DC',
  ruleStrong:   '#CFCBC2',
  paper:        '#FAFAF7',
  paper200:     '#F2F1EB',
  signal:       '#B8451F',
  signal50:     '#FBEDE6',
  signal900:    '#5B2210',
  active500:    '#3F5F3F',
} as const

const TRUNCATED_TOOLTIP =
  'Der er flere ejere eller datterselskaber end grafen viser — grænse nået.'

// ---------------------------------------------------------------------------
// Edge label
// ---------------------------------------------------------------------------

function edgePercentLabel(edge: GraphEdge): string | null {
  return formatPercentRange(edge.capitalPercent, edge.capitalPercentMax)
}

// ---------------------------------------------------------------------------
// SVG sub-components
// ---------------------------------------------------------------------------

interface EdgeStrokeProps {
  d:           string
  highlighted: boolean
  dimmed:      boolean
}

const EdgeStroke: React.FC<EdgeStrokeProps> = ({
  d,
  highlighted,
  dimmed,
}) => {
  const stroke  = highlighted ? C.ink : C.ruleStrong
  const strokeW = highlighted ? 2.25 : 1.5
  const opacity = dimmed ? 0.18 : 1
  return (
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeW}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ opacity }}
    />
  )
}

interface EdgeLabelBadgeProps {
  text:        string
  cx:          number
  cy:          number
  w:           number
  h:           number
  highlighted: boolean
  /** True when some node is hovered — enables strong contrast for this badge vs others */
  hoverActive: boolean
}

const EdgeLabelBadge: React.FC<EdgeLabelBadgeProps> = ({
  text, cx, cy, w, h, highlighted, hoverActive,
}) => {
  const opacity = !hoverActive ? 1 : highlighted ? 1 : 0.1
  const padBoost = highlighted ? 2 : 0
  const rw = w + padBoost * 2
  const rh = h + padBoost * 2
  const fontSize = hoverActive && highlighted ? 11 : 9
  // Thin, non-bold border in all states — the highlight is carried by
  // colour, padding, and font size, not by a heavy stroke.
  const strokeW = 0.85
  const strokeCol = hoverActive && highlighted ? C.ink : C.ruleStrong
  const textCol = hoverActive && highlighted ? C.ink : C.ink500
  return (
    <g style={{ opacity }}>
      <rect
        x={cx - rw / 2}
        y={cy - rh / 2}
        width={rw}
        height={rh}
        rx={hoverActive && highlighted ? 5 : 4}
        fill={C.paper}
        stroke={strokeCol}
        strokeWidth={strokeW}
      />
      <text
        x={cx}
        y={cy + (fontSize >= 11 ? 4.5 : 4)}
        textAnchor="middle"
        fontSize={fontSize}
        fontWeight={400}
        fill={textCol}
        fontFamily="'Inter', ui-sans-serif, sans-serif"
      >
        {text}
      </text>
    </g>
  )
}

// ---------------------------------------------------------------------------

const NODE_FOCUS_RING_PAD = 4

function nodeFocusRingRx(h: number, rx: number, isPerson: boolean): number {
  return isPerson ? (h + NODE_FOCUS_RING_PAD * 2) / 2 : rx + 2
}

/** Hold full opacity after expand, then fade out (seconds). */
const EXPAND_LINGER_HOLD_S = 1.5
const EXPAND_LINGER_FADE_S = 0.5
const EXPAND_LINGER_TOTAL_MS = (EXPAND_LINGER_HOLD_S + EXPAND_LINGER_FADE_S) * 1000

type NodeFocusRingVariant = 'animated' | 'static'

const NodeFocusRing: React.FC<{
  x: number
  y: number
  w: number
  h: number
  rx: number
  isPerson: boolean
  variant: NodeFocusRingVariant
}> = ({ x, y, w, h, rx, isPerson, variant }) => (
  <rect
    x={x - NODE_FOCUS_RING_PAD}
    y={y - NODE_FOCUS_RING_PAD}
    width={w + NODE_FOCUS_RING_PAD * 2}
    height={h + NODE_FOCUS_RING_PAD * 2}
    rx={nodeFocusRingRx(h, rx, isPerson)}
    fill="none"
    stroke={C.signal}
    strokeWidth={1.75}
    strokeDasharray="5 4"
    opacity={variant === 'animated' ? undefined : 0.9}
    data-testid={variant === 'static' ? 'ownership-graph-node-focus-ring' : undefined}
  >
    {variant === 'animated' && (
      <>
        <animate
          attributeName="opacity"
          values="0.35;0.9;0.35"
          dur="1.1s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="stroke-dashoffset"
          values="0;18"
          dur="1.1s"
          repeatCount="indefinite"
        />
      </>
    )}
  </rect>
)

/** Linger ring after expand — CSS animation (SMIL is unreliable inside transformed SVG). */
const ExpandLingerRing: React.FC<{
  x: number
  y: number
  w: number
  h: number
  rx: number
  isPerson: boolean
}> = ({ x, y, w, h, rx, isPerson }) => (
  <rect
    x={x - NODE_FOCUS_RING_PAD}
    y={y - NODE_FOCUS_RING_PAD}
    width={w + NODE_FOCUS_RING_PAD * 2}
    height={h + NODE_FOCUS_RING_PAD * 2}
    rx={nodeFocusRingRx(h, rx, isPerson)}
    fill="none"
    stroke={C.signal}
    strokeWidth={1.75}
    strokeDasharray="5 4"
    className="ownership-graph-linger-ring"
    data-testid="ownership-graph-expand-linger-ring"
  />
)

interface NodeShapeProps {
  node:               GraphNode
  pos:                PositionedNode
  isHovered:          boolean
  isInChain:          boolean
  isDimmed:           boolean
  /** Node detail panel is open for this node */
  isSelected:         boolean
  /** Expand-up/down in progress from this node */
  isExpandLoading:    boolean
  /** Source of a beneficial edge (reel ejer) — always rust-styled in the graph */
  isBeneficialOwner:  boolean
  onHover:            (id: string | null) => void
  onClick:            (e: React.MouseEvent) => void
}

const NodeShape: React.FC<NodeShapeProps> = ({
  node, pos, isHovered, isInChain, isDimmed, isSelected, isExpandLoading, isBeneficialOwner, onHover, onClick,
}) => {
  const { x, y, w, h } = pos
  const isRoot    = !!node.isRoot
  const isForeign = node.kind === 'foreign-company'
  const isPerson  = node.kind === 'person'
  const isTrunc   = !!node.truncated
  const isClickable = !node.isRoot

  const rx = isPerson ? h / 2 : 4

  // Annotated as string so reassignment to other palette literals (paper200,
  // signal50, ink400) is valid — `as const` on C would otherwise narrow types.
  let fillColor: string = C.paper
  let strokeColor: string = isForeign ? C.signal : isRoot ? C.ink : C.ruleStrong
  let strokeW    = isRoot ? 2 : isHovered ? 1.5 : 1
  let textColor: string = isForeign ? C.signal900 : C.ink

  if (isTrunc) {
    fillColor   = C.paper200
    strokeColor = C.ruleStrong
    textColor   = C.ink400
  } else if (isBeneficialOwner) {
    fillColor   = C.signal50
    strokeColor = C.signal
    strokeW     = Math.max(strokeW, 1.75)
    textColor   = C.signal900
  } else if (isForeign) {
    fillColor = C.signal50
  }
  if (isHovered || isInChain) {
    strokeW = Math.max(strokeW, 2)
  }

  const opacity = isDimmed ? 0.3 : 1

  // Truncate long names for display
  const MAX_CHARS = 21
  const rawName = node.navn || '—'
  const displayName = rawName.length > MAX_CHARS
    ? rawName.slice(0, MAX_CHARS - 1) + '…'
    : rawName

  const hasCountry = node.countryCode && node.countryCode !== 'DK'

  const textY = hasCountry
    ? y + h / 2 - 5
    : y + h / 2 + 4

  const ariaLabel = [
    node.navn,
    isRoot ? ' (denne virksomhed)' : '',
    isForeign && node.countryCode ? `, ${node.countryCode}` : '',
    isBeneficialOwner ? ', reel ejer' : '',
  ].join('')

  return (
    <g
      style={{ opacity, cursor: isClickable ? 'pointer' : 'default' }}
      onClick={isClickable ? onClick : undefined}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
      role={isClickable ? 'button' : undefined}
      aria-label={ariaLabel}
      aria-busy={isExpandLoading || undefined}
    >
      {isExpandLoading && (
        <NodeFocusRing
          x={x}
          y={y}
          w={w}
          h={h}
          rx={rx}
          isPerson={isPerson}
          variant="animated"
        />
      )}
      {isSelected && !isExpandLoading && (
        <NodeFocusRing
          x={x}
          y={y}
          w={w}
          h={h}
          rx={rx}
          isPerson={isPerson}
          variant="static"
        />
      )}
      {isTrunc && <title>{TRUNCATED_TOOLTIP}</title>}
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx={rx}
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth={strokeW}
      />

      {/* Main label */}
      <text
        x={x + w / 2}
        y={textY}
        textAnchor="middle"
        fontSize={isPerson ? 11 : 11}
        fontFamily={isPerson
          ? "'Source Serif 4', 'Source Serif Pro', ui-serif, Georgia, serif"
          : "'Inter', ui-sans-serif, sans-serif"
        }
        fontWeight={isRoot ? 600 : 400}
        fill={textColor}
      >
        {displayName}
      </text>

      {/* Country code for foreign/non-DK nodes */}
      {hasCountry && (
        <text
          x={x + w / 2}
          y={y + h / 2 + 9}
          textAnchor="middle"
          fontSize={9}
          fontFamily="'Inter', ui-sans-serif, sans-serif"
          fill={C.signal}
        >
          {node.countryCode}
        </text>
      )}

      {/* Truncated marker */}
      {isTrunc && (
        <text
          x={x + w - 8}
          y={y + h / 2 + 4}
          textAnchor="middle"
          fontSize={9}
          fill={C.ink400}
          fontFamily="'Inter', ui-sans-serif, sans-serif"
          aria-hidden="true"
        >
          …
        </text>
      )}
    </g>
  )
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

const GraphSkeleton: React.FC = () => (
  <div
    className="flex flex-col items-center gap-4 py-6 animate-pulse"
    aria-busy="true"
    aria-label="Indlæser ejerskabsgraf"
    data-testid="ownership-graph-loading"
  >
    <div className="flex gap-6">
      <div className="h-10 w-36 bg-paper-200 rounded" />
      <div className="h-10 w-32 bg-paper-200 rounded-full" />
    </div>
    <div className="h-8 w-0.5 bg-rule" />
    <div className="h-10 w-40 bg-paper-200 rounded" />
    <p className="text-sm text-ink-muted text-center max-w-md px-4 animate-none">
      Henter ejerskabsgraf… Store strukturer kan tage op til et minut.
    </p>
  </div>
)

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface OwnershipGraphProps {
  data:    OwnershipGraphData | null
  loading: boolean
  /** All participant rows for the root company (owners, ledelse, stiftere, …). */
  rootParticipants?: CVRParticipant[]
  beneficialOwners?: CVRParticipant[]
}

interface EdgeGeomRow {
  edge:       GraphEdge
  from:       PositionedNode
  to:         PositionedNode
  d:          string
  geom:       CurvedEdgeGeom
  labelText:  string | null
  lx:         number
  ly:         number
  labelW:     number
  labelH:     number
}

const GraphExpandButton: React.FC<{
  direction: 'up' | 'down'
  cx: number
  y: number
  busy: boolean
  testId: string
  ariaLabel: string
  title: string
  onClick: () => void
}> = ({ direction, cx, y, busy, testId, ariaLabel, title, onClick }) => (
  <g
    role="button"
    tabIndex={busy ? -1 : 0}
    aria-label={busy ? `${ariaLabel} (henter)` : ariaLabel}
    aria-busy={busy}
    data-testid={testId}
    data-busy={busy ? 'true' : 'false'}
    style={{ cursor: busy ? 'wait' : 'pointer' }}
    transform={`translate(${cx}, ${y})`}
    onPointerDown={(e) => e.stopPropagation()}
    onClick={(e) => {
      e.stopPropagation()
      if (!busy) onClick()
    }}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if (!busy) onClick()
      }
    }}
  >
    <title>{busy ? 'Henter…' : title}</title>
    <circle
      r={14}
      fill={C.paper}
      stroke={busy ? C.signal : C.ruleStrong}
      strokeWidth={busy ? 1.5 : 1}
    />
    {busy ? (
      <g>
        <circle
          r={7}
          fill="none"
          stroke={C.signal}
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeDasharray="12 32"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0"
            to="360"
            dur="0.75s"
            repeatCount="indefinite"
          />
        </circle>
      </g>
    ) : (
      <path
        d={direction === 'down' ? 'M-5 -2 L0 4 L5 -2' : 'M-5 4 L0 -2 L5 4'}
        fill="none"
        stroke={C.ink500}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
  </g>
)

interface ExpandPending {
  nodeId: string
  direction: 'up' | 'down'
}

const OwnershipGraph: React.FC<OwnershipGraphProps> = ({
  data,
  loading,
  rootParticipants = [],
  beneficialOwners = [],
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
  const [graphData, setGraphData] = useState<OwnershipGraphData | null>(data)
  const [expandPending, setExpandPending] = useState<ExpandPending | null>(null)
  const [expandLinger, setExpandLinger] = useState<{ nodeId: string; gen: number } | null>(null)
  const syncedRootCvrRef = useRef<string | null | undefined>(undefined)
  const [expandError, setExpandError] = useState<string | null>(null)
  const [personDetails, setPersonDetails] = useState<PersonDetails | null>(null)
  const [personLoading, setPersonLoading] = useState(false)
  const [companyPanelInfo, setCompanyPanelInfo] = useState<{
    startdato: string | null
    subsidiaryCount: number | null
    beneficialOwners: CVRParticipant[]
  } | null>(null)
  const [companyLoading, setCompanyLoading] = useState(false)

  useEffect(() => {
    const nextRoot = data?.rootCvr ?? null
    if (syncedRootCvrRef.current === nextRoot && syncedRootCvrRef.current !== undefined) {
      return
    }
    syncedRootCvrRef.current = nextRoot
    setGraphData(data)
    setExpandError(null)
    setSelectedNodeId(null)
    setExpandPending(null)
    setExpandLinger(null)
  }, [data])

  useEffect(() => {
    if (!expandLinger) return
    const t = window.setTimeout(() => setExpandLinger(null), EXPAND_LINGER_TOTAL_MS)
    return () => clearTimeout(t)
  }, [expandLinger])

  const handleResetGraph = useCallback(() => {
    if (!data) return
    setGraphData(data)
    setExpandError(null)
    setSelectedNodeId(null)
    setExpandPending(null)
    setExpandLinger(null)
    setPersonDetails(null)
    setPersonLoading(false)
    setCompanyPanelInfo(null)
    setCompanyLoading(false)
  }, [data])

  const selectedNode = selectedNodeId && graphData
    ? graphData.nodes.find(n => n.id === selectedNodeId) ?? null
    : null

  useEffect(() => {
    if (!selectedNode?.enhedsNummer || selectedNode.kind !== 'person') {
      setPersonDetails(null)
      setPersonLoading(false)
      return
    }
    let cancelled = false
    setPersonLoading(true)
    setPersonDetails(null)
    getPersonDetails(String(selectedNode.enhedsNummer))
      .then(details => {
        if (!cancelled) setPersonDetails(details)
      })
      .catch(() => {
        if (!cancelled) setPersonDetails(null)
      })
      .finally(() => {
        if (!cancelled) setPersonLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedNode?.id, selectedNode?.enhedsNummer, selectedNode?.kind])

  useEffect(() => {
    if (!selectedNode?.cvrNummer || selectedNode.kind !== 'company') {
      setCompanyPanelInfo(null)
      setCompanyLoading(false)
      return
    }
    let cancelled = false
    setCompanyLoading(true)
    setCompanyPanelInfo(null)
    getCompanyDetails(selectedNode.cvrNummer)
      .then(details => {
        if (!cancelled) {
          setCompanyPanelInfo({
            startdato: details.startdato ?? null,
            subsidiaryCount: typeof details.subsidiaryCount === 'number'
              ? details.subsidiaryCount
              : null,
            beneficialOwners: details.ownership?.beneficialOwners ?? [],
          })
        }
      })
      .catch(() => {
        if (!cancelled) setCompanyPanelInfo(null)
      })
      .finally(() => {
        if (!cancelled) setCompanyLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedNode?.id, selectedNode?.cvrNummer, selectedNode?.kind])

  const layout = useMemo(() => {
    if (!graphData || graphData.nodes.length === 0) return null
    const rootId = 'cvr:' + graphData.rootCvr
    const expandUpNodeIds = graphData.nodes
      .filter(n => canExpandUp(n, rootId, graphData.edges))
      .map(n => n.id)
    const expandDownNodeIds: string[] = []
    if (canExpandDownRoot(graphData)) expandDownNodeIds.push(rootId)
    for (const n of graphData.nodes) {
      if (canExpandDown(n, rootId, graphData.edges)) expandDownNodeIds.push(n.id)
    }
    return layoutTree(graphData.nodes, graphData.edges, rootId, {
      expandDownNodeIds,
      expandUpNodeIds,
    })
  }, [graphData])

  const activeHighlightId = hoveredId ?? selectedNodeId

  const highlightChain = useMemo(() => {
    if (!activeHighlightId || !graphData) return new Set<string>()
    return buildHighlightLineThrough(activeHighlightId, 'cvr:' + graphData.rootCvr, graphData.edges)
  }, [activeHighlightId, graphData])

  const beneficialNodeIds = useMemo(() => {
    if (!graphData) return new Set<string>()
    const s = new Set<string>()
    for (const e of graphData.edges) {
      if (e.kind === 'beneficial') s.add(e.from)
    }
    return s
  }, [graphData])

  const edgeRows = useMemo((): EdgeGeomRow[] => {
    if (!graphData || !layout) return []
    const { positions } = layout
    const lanes = computeLaneOffsetXPerEdge(graphData.edges)

    const raw: EdgeGeomRow[] = []
    for (let i = 0; i < graphData.edges.length; i++) {
      const edge = graphData.edges[i]
      const from = positions[edge.from]
      const to   = positions[edge.to]
      if (!from || !to) continue
      const { offsetX } = lanes[i]
      const intermediates = intermediateNodesSkippedByEdge(
        edge.from,
        edge.to,
        positions,
        graphData.edges,
      )
      const g = buildCurvedOwnerEdge(from, to, offsetX, intermediates)
      const labelText = edgePercentLabel(edge)
      let labelW = 0
      let labelH = 0
      if (labelText) {
        const box = labelBoxForText(labelText, g.lx, g.ly)
        labelW = box.w
        labelH = box.h
      }
      raw.push({
        edge,
        from,
        to,
        d: g.path,
        geom: g,
        labelText,
        lx: g.lx,
        ly: g.ly,
        labelW,
        labelH,
      })
    }

    // Slide labels along each curve so overlapping percentage badges
    // separate while staying centred on their respective edges.
    const placements = deconflictLabelsOnCurves(
      raw.map(r => ({
        edge: r.labelText && r.edge.kind !== 'beneficial' ? r.geom : null,
        w:    r.labelW,
        h:    r.labelH,
      })),
    )
    for (let i = 0; i < raw.length; i++) {
      const p = placements[i]
      if (p && raw[i].labelText && raw[i].edge.kind !== 'beneficial') {
        raw[i].lx = p.cx
        raw[i].ly = p.cy
      }
    }

    return raw
  }, [graphData, layout])

  const handleDownloadSvg = useCallback(() => {
    if (!graphData || !layout) return
    const svg = buildOwnershipGraphSvg({
      graphData,
      positions: layout.positions,
      edgeRows,
      beneficialNodeIds,
      svgWidth: layout.svgWidth,
      svgHeight: layout.svgHeight,
    })
    downloadOwnershipGraphSvg(
      svg,
      ownershipGraphSvgFilename(graphData.rootCvr),
    )
  }, [graphData, layout, edgeRows, beneficialNodeIds])

  async function handleExpandDown(node: GraphNode) {
    if (!graphData || !node.cvrNummer || expandPending) return
    setExpandPending({ nodeId: node.id, direction: 'down' })
    setExpandError(null)
    try {
      const fromCvr = node.isRoot ? undefined : node.cvrNummer
      const downstream = await getOwnershipGraphDownstream(
        graphData.rootCvr,
        graphData.nodes.length,
        fromCvr,
      )
      setGraphData(mergeGraphData(graphData, downstream, {
        clearHasDownstream: !!node.isRoot,
        clearTruncatedOn: node.id,
      }))
      setExpandLinger({ nodeId: node.id, gen: Date.now() })
    } catch {
      setExpandError('Kunne ikke hente datterselskaber.')
    } finally {
      setExpandPending(null)
    }
  }

  async function handleExpandUp(node: GraphNode) {
    if (!graphData || !node.cvrNummer || expandPending) return
    setExpandPending({ nodeId: node.id, direction: 'up' })
    setExpandError(null)
    try {
      const upstream = await getOwnershipGraphUpstream(
        graphData.rootCvr,
        node.cvrNummer,
        graphData.nodes.length,
      )
      setGraphData(mergeGraphData(graphData, upstream, { clearTruncatedOn: node.id }))
      setExpandLinger({ nodeId: node.id, gen: Date.now() })
    } catch {
      setExpandError('Kunne ikke hente ejere over selskabet.')
    } finally {
      setExpandPending(null)
    }
  }

  if (loading) return <GraphSkeleton />
  if (!graphData || !layout || graphData.nodes.length === 0) return null

  const { positions, svgWidth, svgHeight } = layout
  const rootId = 'cvr:' + graphData.rootCvr
  const rootPos = positions[rootId]
  const showExpandDownRoot = canExpandDownRoot(graphData) && !!rootPos
  const showGraph = graphData.edges.length > 0 || !!graphData.hasDownstream

  function handleNodeClick(node: GraphNode, e: React.MouseEvent) {
    e.stopPropagation()
    setSelectedNodeId(node.id)
  }

  const rootNode = graphData.nodes.find(n => n.id === rootId)
  const expandStatus = expandPending
    ? expandPending.direction === 'down'
      ? 'Henter datterselskaber…'
      : 'Henter ejere…'
    : null

  return (
    <div className="mb-1" data-testid="ownership-graph">
      {!showGraph && (
        <p className="text-sm text-ink-500 mt-1 mb-2">
          Ingen registrerede ejere over selskabet.
        </p>
      )}
      {showGraph && (
        <>
        <GraphViewport
          svgWidth={svgWidth}
          svgHeight={svgHeight}
          graphKey={graphData.rootCvr}
          expandStatus={expandStatus}
          onResetGraph={handleResetGraph}
          onDownloadSvg={handleDownloadSvg}
          overlay={
            selectedNode ? (
              <GraphNodePanel
                node={selectedNode}
                rootId={rootId}
                rootNavn={rootNode?.navn || '—'}
                edges={graphData.edges}
                allParticipants={rootParticipants}
                beneficialOwners={beneficialOwners}
                personDetails={personDetails}
                personLoading={personLoading}
                companyStartdato={companyPanelInfo?.startdato}
                companySubsidiaryCount={companyPanelInfo?.subsidiaryCount}
                companyBeneficialOwners={companyPanelInfo?.beneficialOwners}
                companyLoading={companyLoading}
                onClose={() => setSelectedNodeId(null)}
              />
            ) : undefined
          }
        >
          <svg
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            width={svgWidth}
            height={svgHeight}
            className="block"
            role="img"
            aria-label="Ejerskabsgraf"
            onMouseLeave={() => setHoveredId(null)}
          >
            {/* Edge strokes under nodes */}
            <g className="ownership-graph-edges-under">
              {edgeRows.map((row, i) => {
                if (row.edge.kind === 'beneficial') return null
                const highlighted =
                  highlightChain.has(row.edge.from) && highlightChain.has(row.edge.to)
                const dimmed = activeHighlightId !== null && !highlighted
                if (highlighted) return null
                return (
                  <EdgeStroke
                    key={`under-${i}`}
                    d={row.d}
                    highlighted={false}
                    dimmed={dimmed}
                  />
                )
              })}
            </g>

            {graphData.nodes.map((node) => {
              const pos = positions[node.id]
              if (!pos) return null
              const isExpandLoading = expandPending?.nodeId === node.id
              const dimForExpand = !!expandPending && expandPending.nodeId !== node.id
              return (
                <NodeShape
                  key={node.id}
                  node={node}
                  pos={pos}
                  isHovered={hoveredId === node.id}
                  isInChain={highlightChain.has(node.id)}
                  isDimmed={
                    dimForExpand
                    || (activeHighlightId !== null && !highlightChain.has(node.id))
                  }
                  isSelected={selectedNodeId === node.id && expandLinger?.nodeId !== node.id}
                  isExpandLoading={isExpandLoading}
                  isBeneficialOwner={beneficialNodeIds.has(node.id)}
                  onHover={setHoveredId}
                  onClick={(e) => handleNodeClick(node, e)}
                />
              )
            })}

            {graphData.nodes.map((node) => {
              if (!canExpandUp(node, rootId, graphData.edges)) return null
              const pos = positions[node.id]
              if (!pos) return null
              const busy = expandPending?.nodeId === node.id
              return (
                <GraphExpandButton
                  key={`expand-up-${node.id}`}
                  direction="up"
                  cx={pos.x + pos.w / 2}
                  y={pos.y - 22}
                  busy={busy}
                  testId={`ownership-graph-expand-up-${node.cvrNummer}`}
                  ariaLabel={`Vis flere ejere over ${node.navn}`}
                  title="Vis flere ejere (legal ejerskab opad)"
                  onClick={() => handleExpandUp(node)}
                />
              )
            })}

            {graphData.nodes.map((node) => {
              if (!canExpandDown(node, rootId, graphData.edges)) return null
              const pos = positions[node.id]
              if (!pos) return null
              const busy = expandPending?.nodeId === node.id
              return (
                <GraphExpandButton
                  key={`expand-down-${node.id}`}
                  direction="down"
                  cx={pos.x + pos.w / 2}
                  y={pos.y + pos.h + 22}
                  busy={busy}
                  testId={`ownership-graph-expand-down-${node.cvrNummer}`}
                  ariaLabel={`Vis flere datterselskaber under ${node.navn}`}
                  title="Vis flere datterselskaber (legal ejerskab nedad)"
                  onClick={() => handleExpandDown(node)}
                />
              )
            })}

            {showExpandDownRoot && rootPos && rootNode && (
              <GraphExpandButton
                direction="down"
                cx={rootPos.x + rootPos.w / 2}
                y={rootPos.y + rootPos.h + 22}
                busy={expandPending?.nodeId === rootId}
                testId="ownership-graph-expand-down"
                ariaLabel="Vis datterselskaber"
                title="Vis datterselskaber (legal ejerskab nedad)"
                onClick={() => handleExpandDown(rootNode)}
              />
            )}

            {/* Highlighted edge strokes redrawn on top of dimmed ones */}
            <g className="ownership-graph-edges-over">
              {edgeRows.map((row, i) => {
                if (row.edge.kind === 'beneficial') return null
                const highlighted =
                  highlightChain.has(row.edge.from) && highlightChain.has(row.edge.to)
                if (!highlighted) return null
                return (
                  <EdgeStroke
                    key={`over-${i}`}
                    d={row.d}
                    highlighted
                    dimmed={false}
                  />
                )
              })}
            </g>

            {/* Labels sit on top of all edges so percentages stay legible */}
            <g className="ownership-graph-edge-labels">
              {activeHighlightId !== null &&
                edgeRows.map((row, i) => {
                  if (!row.labelText || row.edge.kind === 'beneficial') return null
                  const highlighted =
                    highlightChain.has(row.edge.from) && highlightChain.has(row.edge.to)
                  return (
                    <EdgeLabelBadge
                      key={`lbl-${i}`}
                      text={row.labelText}
                      cx={row.lx}
                      cy={row.ly}
                      w={row.labelW}
                      h={row.labelH}
                      highlighted={highlighted}
                      hoverActive
                    />
                  )
                })}
            </g>

            {expandLinger && (() => {
              const lingerNode = graphData.nodes.find(n => n.id === expandLinger.nodeId)
              const lingerPos = positions[expandLinger.nodeId]
              if (!lingerNode || !lingerPos) return null
              const isPerson = lingerNode.kind === 'person'
              return (
                <g key={`expand-linger-${expandLinger.gen}`} aria-hidden>
                  <ExpandLingerRing
                    x={lingerPos.x}
                    y={lingerPos.y}
                    w={lingerPos.w}
                    h={lingerPos.h}
                    rx={isPerson ? lingerPos.h / 2 : 4}
                    isPerson={isPerson}
                  />
                </g>
              )
            })()}
          </svg>
        </GraphViewport>
        {expandError && (
          <p className="text-xs text-signal mt-1" role="alert">{expandError}</p>
        )}
        </>
      )}
    </div>
  )
}

export default OwnershipGraph
